import { apiRequest } from "@services/apiClient";

/**
 * Fetch audit logs with optional filters
 * @param {Object} filters - Query parameters
 * @param {string} filters.start_date - ISO date string (YYYY-MM-DD)
 * @param {string} filters.end_date - ISO date string (YYYY-MM-DD)
 * @param {string} filters.action - CREATE|READ|UPDATE|DELETE|EXPORT|ALL
 * @param {string} filters.resource_type - patient|file|template|user|appointment|ALL
 * @returns {Promise<Object>} - { data: { logs: AuditLog[], pagination: {...} } }
 */
export const fetchAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.action && filters.action !== "ALL") {
    params.append("action", filters.action);
  }
  if (filters.resource_type && filters.resource_type !== "ALL") {
    params.append("resource_type", filters.resource_type);
  }

  const queryString = params.toString();
  const endpoint = `/audit/logs${queryString ? `?${queryString}` : ""}`;

  return apiRequest({
    method: "GET",
    endpoint,
  });
};

/**
 * Export audit logs as CSV
 * @param {Object} filters - Same filters as fetchAuditLogs
 * @returns {Promise<Blob>} - CSV file blob
 */
export const exportAuditLogsCSV = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.action && filters.action !== "ALL") {
    params.append("action", filters.action.toLowerCase());
  }
  if (filters.resource_type && filters.resource_type !== "ALL") {
    params.append("resource_type", filters.resource_type);
  }

  const queryString = params.toString();
  const endpoint = `/audit/logs/export/csv${queryString ? `?${queryString}` : ""}`;

  const token = localStorage.getItem("access_token");
  const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to export CSV");
  }

  return response.blob();
};

/**
 * Export audit logs as JSON
 * @param {Object} filters - Same filters as fetchAuditLogs
 * @returns {Promise<Blob>} - JSON file blob
 */
export const exportAuditLogsJSON = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.action && filters.action !== "ALL") {
    params.append("action", filters.action.toLowerCase());
  }
  if (filters.resource_type && filters.resource_type !== "ALL") {
    params.append("resource_type", filters.resource_type);
  }

  const queryString = params.toString();
  const endpoint = `/audit/logs/export/json${queryString ? `?${queryString}` : ""}`;

  const token = localStorage.getItem("access_token");
  const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  const response = await fetch(`${backendUrl}${endpoint}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to export JSON");
  }

  return response.blob();
};
